/**
 * How a registration's fee reads: one label and one figure, derived together.
 *
 * They used to be worked out separately — the label from `payment_status`, the
 * figure from `amount_paid` — which let them contradict each other on the same
 * card ("Paid / Free", "Amount due / Free").
 *
 * Two rules the split got wrong:
 *
 *  1. **Nothing is free because we failed to store what it cost.** A missing or
 *     zero amount means we do not know, not that nobody paid.
 *
 *  2. **`payment_status = 'free'` does not always mean the seat was free.** Two
 *     back-fill paths write it on registrations for events that DO charge:
 *     CpdExternalSubmissionController, when an admin approves a member's CPD
 *     claim for an event they attended (`CPD-…`), and the legacy attendance
 *     import (`LEG-…`). Those delegates paid — at the venue, or through a
 *     system that predates the portal — and the row was raised afterwards from
 *     an attendance record that knows nothing about money. Printing "Free"
 *     against them tells a member who paid that they did not.
 *
 * An event that charges nothing returns **null**, and the caller prints nothing
 * at all. A row reading "Fee: Free" is noise on a free event — there is no fee
 * to report, and the absence says it better than the word does.
 */

export type FeeTone = "due" | "settled" | "unknown";

export type FeeDisplay = {
  /** The word above the figure: Fee, Outstanding, Paid. */
  label: string;
  /** The figure, "Paid offline", or "—" where nothing was recorded. */
  value: string;
  tone: FeeTone;
};

const naira = (n: number) => `₦${Number(n).toLocaleString()}`;

/**
 * Registration numbers raised by a back-fill rather than a checkout.
 *
 * `CPD-` is CpdExternalSubmissionController's prefix and `LEG-` the legacy
 * import's; both are written in exactly one place each, so the prefix is a
 * reliable marker of "this row came from an attendance record".
 */
const BACKFILLED = /^(CPD|LEG)-/i;

export function registrationFee(reg: any, event: any = reg?.event ?? {}): FeeDisplay | null {
  const amount    = reg?.amount_paid != null ? Number(reg.amount_paid) : null;
  const hasAmount = amount != null && amount > 0;
  const status    = String(reg?.payment_status ?? "");

  // The event charges nothing — there is no fee to report.
  if (event?.is_paid === false) {
    return null;
  }

  // Not positively known to charge (a payload without is_paid) — the old
  // reading of a `free` status still applies.
  const eventCharges = event?.is_paid === true;

  if (status === "free") {
    if (!eventCharges) {
      return null;
    }
    // A charging event with a `free` registration never went through checkout.
    return BACKFILLED.test(String(reg?.registration_no ?? ""))
      ? { label: "Fee", value: "Paid offline", tone: "settled" }
      : { label: "Fee", value: "Not recorded", tone: "unknown" };
  }

  if (status === "pending") {
    // An outstanding fee we cannot put a figure to is still outstanding — say
    // that rather than quietly showing nothing owed.
    return { label: "Outstanding", value: hasAmount ? naira(amount!) : "Due", tone: "due" };
  }

  if (status === "paid") {
    return { label: "Paid", value: hasAmount ? naira(amount!) : "—", tone: hasAmount ? "settled" : "unknown" };
  }

  // failed, or a status we do not recognise.
  return { label: "Fee", value: hasAmount ? naira(amount!) : "—", tone: "unknown" };
}

/** The colour the figure is set in, matching its meaning. */
export const FEE_TONE_CLASSES: Record<FeeTone, string> = {
  due:     "text-amber-600",
  settled: "text-gray-900",
  unknown: "text-gray-400",
};
